// Signing in, a step at a time: each call answers with the next step, until the person is signed in. Owners sign in
// with their password (and set a new one, if TUNE made their account); staff then set up an authenticator app, or
// give its code, as the staff pool requires.
import type { PoolConfig } from '@tune/shared';
import { CognitoError, type AuthResult, type Cognito, type Initiated } from './cognito';

export type Step =
  | { kind: 'done'; result: AuthResult }
  /** A first sign-in with the password TUNE emailed: a new one now. */
  | { kind: 'newPassword'; session: string; email: string }
  /** Staff, first time: set up an authenticator app. */
  | { kind: 'mfaSetup'; session: string; email: string }
  /** Staff: the code from their authenticator app. */
  | { kind: 'mfaCode'; session: string; email: string }
  /** Signed up, but the email's code wasn't given yet. */
  | { kind: 'unconfirmed'; email: string }
  /** TUNE asked for a new password: reset it with an emailed code. */
  | { kind: 'resetRequired'; email: string };

/** The step a sign-in's answer leads to. */
function stepOf(answer: Initiated, email: string): Step {
  if (answer.AuthenticationResult) {
    return { kind: 'done', result: answer.AuthenticationResult };
  }
  switch (answer.ChallengeName) {
    case 'NEW_PASSWORD_REQUIRED':
      return { kind: 'newPassword', session: answer.Session, email };
    case 'MFA_SETUP':
      return { kind: 'mfaSetup', session: answer.Session, email };
    case 'SOFTWARE_TOKEN_MFA':
      return { kind: 'mfaCode', session: answer.Session, email };
    default:
      throw new CognitoError('UnsupportedChallenge', `a step TUNE Analytics doesn't have: ${answer.ChallengeName}`);
  }
}

/** An email as Cognito knows it: trimmed and in lower case. */
export function cleanEmail(email: string): string {
  return email.trim().toLowerCase();
}

export interface Flows {
  signIn(email: string, password: string): Promise<Step>;
  newPassword(step: { session: string; email: string }, password: string): Promise<Step>;
  /** The authenticator's key, to show as a QR code and in words. */
  startSetup(step: { session: string }): Promise<{ secret: string; session: string }>;
  finishSetup(step: { session: string; email: string }, code: string): Promise<Step>;
  mfaCode(step: { session: string; email: string }, code: string): Promise<Step>;
  /** Signs up: confirmed at once with an invite for that email, or waiting for the emailed code. */
  signUp(email: string, password: string, name: string, invite?: string): Promise<{ confirmed: boolean }>;
  confirm(email: string, code: string): Promise<void>;
  resend(email: string): Promise<void>;
  forgot(email: string): Promise<void>;
  reset(email: string, code: string, password: string): Promise<void>;
}

export function flows(api: Cognito, pool: PoolConfig): Flows {
  const id = pool.clientId;
  return {
    async signIn(email, password) {
      const who = cleanEmail(email);
      try {
        return stepOf(await api.initiateAuth(id, who, password), who);
      } catch (e) {
        if (e instanceof CognitoError && e.type === 'UserNotConfirmedException') {
          return { kind: 'unconfirmed', email: who };
        }
        if (e instanceof CognitoError && e.type === 'PasswordResetRequiredException') {
          return { kind: 'resetRequired', email: who };
        }
        throw e;
      }
    },
    async newPassword(step, password) {
      return stepOf(
        await api.respond(id, 'NEW_PASSWORD_REQUIRED', step.session, { USERNAME: step.email, NEW_PASSWORD: password }),
        step.email,
      );
    },
    async startSetup(step) {
      const answer = await api.associateToken(step.session);
      return { secret: answer.SecretCode, session: answer.Session ?? step.session };
    },
    async finishSetup(step, code) {
      const verified = await api.verifyToken(step.session, code.trim());
      return stepOf(
        await api.respond(id, 'MFA_SETUP', verified.Session ?? step.session, { USERNAME: step.email }),
        step.email,
      );
    },
    async mfaCode(step, code) {
      return stepOf(
        await api.respond(id, 'SOFTWARE_TOKEN_MFA', step.session, {
          USERNAME: step.email,
          SOFTWARE_TOKEN_MFA_CODE: code.trim(),
        }),
        step.email,
      );
    },
    async signUp(email, password, name, invite) {
      const answer = await api.signUp(
        id,
        cleanEmail(email),
        password,
        { email: cleanEmail(email), ...(name.trim() ? { name: name.trim() } : {}) },
        invite ? { invite } : undefined,
      );
      return { confirmed: answer.UserConfirmed };
    },
    async confirm(email, code) {
      await api.confirmSignUp(id, cleanEmail(email), code.trim());
    },
    async resend(email) {
      await api.resendCode(id, cleanEmail(email));
    },
    async forgot(email) {
      await api.forgotPassword(id, cleanEmail(email));
    },
    async reset(email, code, password) {
      await api.confirmForgotPassword(id, cleanEmail(email), code.trim(), password);
    },
  };
}

/** The address an authenticator app takes: TUNE's name for it, the person's email, and the key. */
export function otpauthUri(email: string, secret: string): string {
  return `otpauth://totp/${encodeURIComponent(`TUNE:${email}`)}?secret=${secret}&issuer=TUNE&algorithm=SHA1&digits=6&period=30`;
}

/** A key as people copy it: in groups of four. */
export function groupKey(secret: string): string {
  return secret.replace(/(.{4})/g, '$1 ').trim();
}
